import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Download, QrCode, MessageCircle, Users } from "lucide-react";
import { XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { notify } from "@/lib/toast";
import TrialBanner from "@/components/TrialBanner";
import { logError } from "@/lib/logger";

const CHART_TOOLTIP_STYLE = {
    background: "rgba(0,0,0,0.8)",
    border: "1px solid rgba(16,185,129,0.3)",
    borderRadius: 8,
};

const EMPTY_STATS = { total_leads: 0, total_scans: 0, today_leads: 0, week_leads: 0, conversion: 0, series: [] };

function leadsToCsv(leads) {
    let csv = "Name,Phone,Email,Message,Date\n";
    leads.forEach((l) => {
        const row = [l.name, l.phone, l.email || "", (l.message || "").replace(/"/g, '""'), new Date(l.timestamp).toLocaleString()];
        csv += row.map((v) => `"${v}"`).join(",") + "\n";
    });
    return csv;
}

function downloadCsv(csv, filename) {
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
}

function filterLeads(leads, search) {
    const q = search.toLowerCase();
    if (!q) return leads;
    return leads.filter((l) =>
        l.name?.toLowerCase().includes(q) || l.phone?.includes(q) || l.email?.toLowerCase().includes(q),
    );
}

async function fetchDashboardData() {
    const [leadsRes, statsRes] = await Promise.all([
        api.get("/leads"),
        api.get("/stats"),
    ]);
    return { leads: leadsRes.data || [], stats: statsRes.data };
}

export default function Dashboard() {
    const { user } = useAuth();
    const userId = user?.id;
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState(EMPTY_STATS);
    const [search, setSearch] = useState("");

    useEffect(() => {
        let cancelled = false;
        if (!userId) return;
        fetchDashboardData()
            .then(({ leads: ls, stats: s }) => {
                if (cancelled) return;
                setLeads(ls);
                setStats(s);
            })
            .catch((err) => {
                logError("Failed to load dashboard data", err);
                notify("Failed to load dashboard data", "error");
            });
        return () => { cancelled = true; };
    }, [userId]);

    const filtered = useMemo(() => filterLeads(leads, search), [leads, search]);

    function exportCSV() {
        if (leads.length === 0) { notify("No leads to export", "error"); return; }
        downloadCsv(leadsToCsv(leads), `connexa-leads-${user?.slug}-${new Date().toISOString().split("T")[0]}.csv`);
        notify("Leads exported!");
    }

    return (
        <section className="min-h-screen pt-40 pb-16 mesh-gradient" data-testid="dashboard-page">
            <div className="max-w-7xl mx-auto px-6">
                <TrialBanner />
                <DashboardHeader user={user} onExport={exportCSV} />
                <StatGrid stats={stats} />
                <LeadsChart series={stats.series} />
                <LeadsTablePanel
                    leads={filtered}
                    search={search}
                    onSearch={setSearch}
                />
            </div>
        </section>
    );
}

function DashboardHeader({ user, onExport }) {
    const isPaid = user?.plan && !["trial", "free"].includes(user.plan);
    return (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10 gap-4">
            <div>
                <h1 className="font-display font-bold text-3xl mb-2">Lead Dashboard</h1>
                <p className="text-gray-400">Manage and analyze your WhatsApp leads</p>
            </div>
            <div className="flex gap-3">
                {isPaid ? (
                    <button onClick={onExport} className="btn-primary px-6 py-3 rounded-xl font-semibold text-white flex items-center gap-2" data-testid="export-csv">
                        <Download className="w-5 h-5" /> Export CSV
                    </button>
                ) : (
                    <button onClick={() => notify("CSV export is a Growth/Pro feature — upgrade to unlock", "info")}
                        className="glass px-6 py-3 rounded-xl font-semibold text-gray-400 flex items-center gap-2 opacity-70 cursor-not-allowed" data-testid="export-csv-locked">
                        <Download className="w-5 h-5" /> Export CSV 🔒
                    </button>
                )}
                <Link to="/qr" className="glass px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition-all flex items-center gap-2" data-testid="nav-to-qr">
                    <QrCode className="w-5 h-5" /> My QR
                </Link>
            </div>
        </div>
    );
}

function StatGrid({ stats }) {
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total Leads" value={stats.total_leads} hint="All time" testid="dash-total" />
            <StatCard label="Today" value={stats.today_leads} hint="Last 24h" testid="dash-today" />
            <StatCard label="This Week" value={stats.week_leads} hint="Last 7 days" testid="dash-week" />
            <StatCard label="Conversion" value={`${stats.conversion}%`} hint="Leads / Scans" testid="dash-conv" />
        </div>
    );
}

function LeadsChart({ series }) {
    return (
        <div className="glass rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-lg mb-4">Leads Over Time</h3>
            <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={series}>
                        <defs>
                            <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="label" stroke="#6b7280" fontSize={11} />
                        <YAxis allowDecimals={false} stroke="#6b7280" fontSize={11} />
                        <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                        <Area type="monotone" dataKey="count" stroke="#10b981" fill="url(#g1)" strokeWidth={2} />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function LeadsTablePanel({ leads, search, onSearch }) {
    return (
        <div className="glass rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-white/5 flex items-center justify-between">
                <h3 className="font-semibold text-lg">Recent Leads</h3>
                <input value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search leads..."
                    className="input-field px-4 py-2 rounded-lg text-sm" data-testid="leads-search" />
            </div>
            {leads.length === 0 ? <NoLeadsEmpty /> : <LeadsTable leads={leads} />}
        </div>
    );
}

function NoLeadsEmpty() {
    return (
        <div className="p-12 text-center" data-testid="no-leads">
            <Users className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400 text-lg">No leads yet</p>
            <p className="text-gray-500 text-sm mt-2">Share your QR code to start collecting leads</p>
        </div>
    );
}

function LeadsTable({ leads }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full">
                <thead>
                    <tr className="text-left text-xs text-gray-400 uppercase tracking-wider border-b border-white/5">
                        <th className="px-6 py-4 font-medium">Name</th>
                        <th className="px-6 py-4 font-medium">Phone</th>
                        <th className="px-6 py-4 font-medium">Email</th>
                        <th className="px-6 py-4 font-medium">Message</th>
                        <th className="px-6 py-4 font-medium">Date</th>
                        <th className="px-6 py-4 font-medium">Action</th>
                    </tr>
                </thead>
                <tbody data-testid="leads-tbody">
                    {leads.map((l) => <LeadRow key={l.id} lead={l} />)}
                </tbody>
            </table>
        </div>
    );
}

function LeadRow({ lead }) {
    function chat() {
        window.open(`https://wa.me/${lead.phone}?text=${encodeURIComponent("Hi! Thanks for scanning my QR code. How can I help you?")}`, "_blank");
    }
    return (
        <tr className="lead-row border-b border-white/5 transition-colors">
            <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-brand-500/20 flex items-center justify-center text-brand-400 text-sm font-bold">{(lead.name || "?").charAt(0).toUpperCase()}</div>
                    <span className="font-medium text-sm">{lead.name}</span>
                </div>
            </td>
            <td className="px-6 py-4 text-sm text-gray-300">{lead.phone}</td>
            <td className="px-6 py-4 text-sm text-gray-300">{lead.email || <span className="text-gray-600">-</span>}</td>
            <td className="px-6 py-4 text-sm text-gray-400 max-w-xs truncate">{lead.message || <span className="text-gray-600">-</span>}</td>
            <td className="px-6 py-4 text-sm text-gray-400 whitespace-nowrap">{new Date(lead.timestamp).toLocaleString()}</td>
            <td className="px-6 py-4">
                <button onClick={chat} className="whatsapp-btn px-3 py-1.5 rounded-lg text-xs font-semibold text-white inline-flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" /> Chat
                </button>
            </td>
        </tr>
    );
}

function StatCard({ label, value, hint, testid }) {
    return (
        <div className="stat-card glass rounded-2xl p-5">
            <div className="text-sm text-gray-400 mb-1">{label}</div>
            <div className="text-3xl font-bold gradient-text" data-testid={testid}>{value}</div>
            <div className="text-xs text-gray-500 mt-2">{hint}</div>
        </div>
    );
}
